/**
 * List import parsers
 *
 * Delegates text line parsing to shared parseCardLine().
 * Keeps list-specific: JSON backup parsing, CSV with priority/notes, types, mergeDuplicateCards.
 */
import { parseCardLine } from '@/lib/cardLineParser';
import type { Finish } from '@/types/card';
import type { ListPriority } from '@/types/lists';

import type { ListCardBackup, MultiListBackup, SingleListBackup } from '@/features/import-export/types';

// ============================================================================
// Types
// ============================================================================

export type ListImportFormat = 'mtgcraft' | 'mtgcraft-multi' | 'text' | 'csv';

export interface ParsedListMeta {
  name: string;
  icon: string | null;
  color: string | null;
}

export interface ParsedListCard {
  name: string;
  oracle_id?: string;
  scryfall_id?: string;
  setCode?: string | null;
  collectorNumber?: string | null;
  quantity: number;
  finish?: Finish;
  priority?: ListPriority;
  notes?: string | null;
}

export interface ParsedListImport {
  format: ListImportFormat;
  listMeta?: ParsedListMeta;
  cards: ParsedListCard[];
  errors: string[];
}

export interface ParsedMultiListImport {
  format: 'mtgcraft-multi';
  lists: Array<{ meta: ParsedListMeta; cards: ParsedListCard[] }>;
  errors: string[];
}

// ============================================================================
// Format Detection
// ============================================================================

export function detectListFormat(text: string): ListImportFormat | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed.type === 'mtgcraft_lists' && Array.isArray(parsed.lists)) return 'mtgcraft-multi';
      if (parsed.type === 'mtgcraft_list' && parsed.list) return 'mtgcraft';
    } catch {
      /* not JSON */
    }
  }

  const firstLine = trimmed.split('\n')[0];
  if (firstLine.includes(',') && (firstLine.toLowerCase().includes('name') || firstLine.toLowerCase().includes('quantity'))) {
    return 'csv';
  }

  return 'text';
}

// ============================================================================
// Public API
// ============================================================================

export function parseListImport(text: string, format?: ListImportFormat): ParsedListImport {
  const trimmed = text.trim();
  const fmt = format ?? detectListFormat(trimmed);
  if (!fmt) return { format: 'text', cards: [], errors: ['Could not detect import format.'] };

  switch (fmt) {
    case 'mtgcraft':
      return parseMtgCraftJson(trimmed);
    case 'mtgcraft-multi': {
      const multi = parseMtgCraftMultiJson(trimmed);
      if (multi.lists.length > 0) {
        return { format: 'mtgcraft', listMeta: multi.lists[0].meta, cards: multi.lists[0].cards, errors: multi.errors };
      }
      return { format: 'mtgcraft', cards: [], errors: multi.errors };
    }
    case 'csv':
      return parseCsv(trimmed);
    case 'text':
      return parseText(trimmed);
  }
}

export function parseMultiListImport(text: string): ParsedMultiListImport {
  const trimmed = text.trim();
  const fmt = detectListFormat(trimmed);

  if (fmt === 'mtgcraft-multi') return parseMtgCraftMultiJson(trimmed);
  if (fmt === 'mtgcraft') {
    const single = parseMtgCraftJson(trimmed);
    if (single.listMeta && single.cards.length > 0) {
      return { format: 'mtgcraft-multi', lists: [{ meta: single.listMeta, cards: single.cards }], errors: single.errors };
    }
  }
  return { format: 'mtgcraft-multi', lists: [], errors: ['Invalid multi-list backup format.'] };
}

export function mergeDuplicateCards(cards: ParsedListCard[]): ParsedListCard[] {
  const map = new Map<string, ParsedListCard>();
  for (const card of cards) {
    const key = card.name.toLowerCase();
    const existing = map.get(key);
    if (existing) {
      existing.quantity += card.quantity;
      if (card.priority === 'high' || (card.priority === 'medium' && existing.priority === 'low')) existing.priority = card.priority;
      if (card.notes && !existing.notes) existing.notes = card.notes;
    } else {
      map.set(key, { ...card });
    }
  }
  return Array.from(map.values());
}

// ============================================================================
// JSON Parsers
// ============================================================================

function parseBackupCard(card: ListCardBackup, label: string, errors: string[]): ParsedListCard | null {
  if (!card.name) {
    errors.push(`${label}: missing name`);
    return null;
  }
  return {
    name: card.name,
    oracle_id: card.oracle_id,
    scryfall_id: card.scryfall_id ?? undefined,
    quantity: card.quantity ?? 1,
    priority: isValidPriority(card.priority) ? card.priority : 'medium',
    notes: card.notes ?? null,
  };
}

function parseMtgCraftJson(text: string): ParsedListImport {
  try {
    const data = JSON.parse(text) as SingleListBackup;
    if (!data.list?.cards || !Array.isArray(data.list.cards)) {
      return { format: 'mtgcraft', cards: [], errors: ['Invalid MtgCraft JSON: missing list.cards array'] };
    }
    const errors: string[] = [];
    const listMeta: ParsedListMeta = {
      name: data.list.name ?? 'Imported List',
      icon: data.list.icon ?? null,
      color: data.list.color ?? null,
    };
    const cards = data.list.cards
      .map((c, i) => parseBackupCard(c, `Card at index ${i}`, errors))
      .filter((c): c is ParsedListCard => c !== null);
    return { format: 'mtgcraft', listMeta, cards, errors };
  } catch (e) {
    return { format: 'mtgcraft', cards: [], errors: [`Invalid JSON: ${e instanceof Error ? e.message : 'Unknown error'}`] };
  }
}

function parseMtgCraftMultiJson(text: string): ParsedMultiListImport {
  try {
    const data = JSON.parse(text) as MultiListBackup;
    if (!data.lists || !Array.isArray(data.lists)) {
      return { format: 'mtgcraft-multi', lists: [], errors: ['Invalid Multi-List JSON: missing lists array'] };
    }
    const errors: string[] = [];
    const lists = data.lists.map((listData, idx) => {
      const meta: ParsedListMeta = {
        name: listData.name ?? `Imported List ${idx + 1}`,
        icon: listData.icon ?? null,
        color: listData.color ?? null,
      };
      const cards = (listData.cards ?? [])
        .map((c, ci) => parseBackupCard(c, `List "${meta.name}", card at index ${ci}`, errors))
        .filter((c): c is ParsedListCard => c !== null);
      return { meta, cards };
    });
    return { format: 'mtgcraft-multi', lists, errors };
  } catch (e) {
    return { format: 'mtgcraft-multi', lists: [], errors: [`Invalid JSON: ${e instanceof Error ? e.message : 'Unknown error'}`] };
  }
}

// ============================================================================
// CSV Parser (list-specific: handles priority + notes columns)
// ============================================================================

function parseCsv(text: string): ParsedListImport {
  const lines = text.split('\n').filter((l) => l.trim());
  if (lines.length < 2) return { format: 'csv', cards: [], errors: ['CSV file appears to be empty'] };

  const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase().trim());
  const col = (names: string[]) => {
    for (const n of names) {
      const i = headers.indexOf(n);
      if (i !== -1) return i;
    }
    return -1;
  };
  const nameIdx = col(['name', 'card name', 'card', 'cardname']);
  const qtyIdx = col(['quantity', 'qty', 'count', 'total quantity', 'copies', 'amount']);
  const prioIdx = col(['priority', 'prio']);
  const noteIdx = col(['notes', 'note', 'comments', 'comment']);

  if (nameIdx === -1) return { format: 'csv', cards: [], errors: ['CSV missing required "Name" column'] };

  const errors: string[] = [];
  const cards: ParsedListCard[] = [];
  for (let i = 1; i < lines.length; i++) {
    const vals = parseCsvLine(lines[i]);
    const name = vals[nameIdx]?.trim();
    if (!name) {
      errors.push(`Row ${i + 1}: Missing card name`);
      continue;
    }
    cards.push({
      name,
      quantity: qtyIdx !== -1 ? parseInt(vals[qtyIdx], 10) || 1 : 1,
      priority: parsePriority(prioIdx !== -1 ? vals[prioIdx]?.toLowerCase().trim() : undefined),
      notes: noteIdx !== -1 ? vals[noteIdx]?.trim() || null : null,
    });
  }
  return { format: 'csv', cards, errors };
}

// ============================================================================
// Text Parser (delegates to shared parseCardLine)
// ============================================================================

const SECTION_HEADER_RE =
  /^(creatures?|instants?|sorceries?|lands?|enchantments?|artifacts?|planeswalkers?|sideboard|maybeboard|commander)$/i;

function parseText(text: string): ParsedListImport {
  const errors: string[] = [];
  const cards: ParsedListCard[] = [];

  for (const [i, raw] of text.split('\n').entries()) {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) continue;
    if (trimmed.endsWith(':') || SECTION_HEADER_RE.test(trimmed)) continue;

    const parsed = parseCardLine(trimmed);
    if (parsed && parsed.quantity > 0) {
      const finish: Finish | undefined = parsed.finish !== 'nonfoil' ? parsed.finish : undefined;
      cards.push({
        name: parsed.name,
        setCode: parsed.setCode,
        collectorNumber: parsed.collectorNumber,
        quantity: parsed.quantity,
        ...(finish && { finish }),
        priority: 'medium',
      });
    } else if (!parsed) {
      errors.push(`Line ${i + 1}: Invalid format "${trimmed}"`);
    }
  }
  return { format: 'text', cards, errors };
}

// ============================================================================
// Helpers
// ============================================================================

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function parsePriority(value: string | undefined): ListPriority {
  if (!value) return 'medium';
  switch (value.toLowerCase()) {
    case 'high':
    case 'h':
    case '1':
      return 'high';
    case 'low':
    case 'l':
    case '3':
      return 'low';
    default:
      return 'medium';
  }
}

function isValidPriority(value: unknown): value is ListPriority {
  return value === 'high' || value === 'medium' || value === 'low';
}
